import './TimelineTemplates.scss';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStateSelector } from '../../../../store/selectors';
import Helpers from '../../../../utils/helper';
import BootstrapTable from '../../../../components/react-bootstrap-table-next/react-bootstrap-table2';
import filterFactory from '../../../../components/react-bootstrap-table-next/react-bootstrap-table2-filter';
import paginationFactory from '../../../../components/react-bootstrap-table-next/react-bootstrap-table2-paginator';
import { TableFilter } from '../../../../components/Table/models/table-filter';
import createNotification from '../../../../utils/createNotification';
import ConfirmationModal from '../../../../components/Modal/ConfirmationModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCopy,
    faEdit,
    faEye,
    faTrashCan,
} from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';
import TimelineCopyModal from '../../Modals/TimelineCopyModal/TimelineCopyModal';
import { Button } from 'reactstrap';
import { useDispatch } from 'react-redux';
import { GetTimelines } from '../../../../slices/marketplace/timeline/thunks/get-timeline-templates.thunk';
import { Timeline } from '../../../../slices/marketplace/timeline/models/timeline.model';
import { resetCurrentTimeline } from '../../../../slices/marketplace/timeline/timeline.slice';
import {
    LevelOfService,
    LevelsOfService,
} from '../../../../slices/marketplace/timeline/models/enums/level-of-service.enum';
import { Tooltip } from 'react-tooltip';
import sanitizeHtml from 'sanitize-html';

const TimelineTemplates = () => {
    const dispatch = useDispatch() as any;
    const navigate = useNavigate();

    const [page, setPage] = useState(1);
    const [totalSize, setTotalSize] = useState(1);
    const [sizePerPage, setSizePerPage] = useState(5);
    const [templates, setTemplates] = useState<Timeline[]>([]);
    const [selectedTemplate, setSelectedTemplate] = useState<Timeline>();

    const axios = useStateSelector((state) => state.core.axios);
    const timelineTemplatesTable = useStateSelector(
        (state) => state.timelines.timelineTemplatesTable
    );

    const [isLoading, setIsLoading] = useState(true);

    const [isRenamingPopupVisible, setIsRenamingPopupVisible] = useState(false);
    const [
        isRemoveConfirmationPopupVisible,
        setIsRemoveConfirmationPopupVisible,
    ] = useState(false);

    const defaultSorted: [{ dataField: any; order: any }] = [
        {
            dataField: 'name',
            order: 'asc',
        },
    ];
    const [currentSort, setCurrentSort] = useState<TableFilter>(
        new TableFilter({ filters: [], logic: '' }, page, sizePerPage, {
            dir: 'asc',
            field: 'name',
        })
    );

    const handleTableChange = (_type: any, newState: any) => {
        const { page, sizePerPage, sortField, sortOrder } = newState;

        const updatedPostObj = new TableFilter(
            { filters: [], logic: '' },
            page,
            sizePerPage,
            { dir: sortOrder, field: sortField }
        );
        setCurrentSort(updatedPostObj);
    };

    const removeTemplate = (templateId: number) => {
        axios
            .delete(`api/Marketplace/TimelineTemplates/${templateId}`)
            .catch(() => {
                createNotification(
                    'An unexpected error occurred while removing Timeline Template.',
                    'error'
                );
            })
            .finally(() => fetchTimelineTemplates());
    };

    const fetchTimelineTemplates = async () => {
        await dispatch(GetTimelines(currentSort));
    };

    useEffect(() => {
        fetchTimelineTemplates()
            .then()
            .finally(() => setIsLoading(false));
    }, []);

    useEffect(() => {
        fetchTimelineTemplates();
    }, [currentSort]);

    useEffect(() => {
        setTemplates(timelineTemplatesTable.timelines);
        setPage(timelineTemplatesTable.pageNumber);
        setTotalSize(timelineTemplatesTable.totalItems);
        setSizePerPage(timelineTemplatesTable.pageSize);
    }, [fetchTimelineTemplates]);

    const templatesColumns = [
        {
            dataField: 'name',
            text: 'Template Name',
            headerClasses: 'name-column-header',
            sort: true,
            formatter: (cellContent: string, row: Timeline) => {
                return (
                    <div>
                        <span
                            onClick={() => {
                                navigate(
                                    `/marketplace/admin/templates/${row.id}`
                                );
                            }}>
                            <span className="referenced-text">
                                {cellContent}
                            </span>
                        </span>
                    </div>
                );
            },
        },

        {
            dataField: 'levelOfService',
            text: 'Type',
            headerClasses: 'type-column-header',
            sort: true,
            formatter: (
                cellContent: LevelOfService,
                row: any,
                index: number
            ) => {
                const value = LevelsOfService.find(
                    (item) => item.id === cellContent
                );
                return value ? (
                    <div className="d-flex justify-content-start" key={index}>
                        <span
                            id={'tab-info-' + index}
                            className="tooltip-target"
                            data-tooltip-html={sanitizeHtml(value.name)}>
                            {value.shortName}{' '}
                        </span>
                        <Tooltip
                            anchorId={'tab-info-' + index}
                            place="top"
                            className="tooltip-content place-top mt-m1"
                            classNameArrow="arrow"
                            offset={0}
                        />
                    </div>
                ) : (
                    'NA'
                );
            },
        },
        {
            dataField: 'dateModified',
            text: 'Last Edited',
            headerClasses: 'last-edited-column-header',
            sort: true,
            formatter: (cellContent: string) => (
                <div>
                    {cellContent
                        ? moment(cellContent).format('M/D/YYYY h:mmA')
                        : '-'}
                </div>
            ),
        },
        {
            dataField: '',
            text: 'Actions',
            headerClasses: 'actions-column-header',
            headerFormatter: () => (
                <div className="d-flex justify-content-between w-100">
                    <div className="w-100">Actions</div>
                </div>
            ),
            style: {
                minWidth: 350,
            },
            formatter: (_cellContent: number, row: Timeline) => {
                return (
                    <>
                        <div className="action-icons">
                            <FontAwesomeIcon
                                onClick={() => {
                                    setSelectedTemplate(row);
                                    setIsRenamingPopupVisible(true);
                                }}
                                icon={faCopy}
                            />
                            <FontAwesomeIcon
                                onClick={() => {
                                    dispatch(resetCurrentTimeline());
                                    navigate(
                                        `/marketplace/admin/templates/${row.id}`
                                    );
                                }}
                                icon={faEdit}
                            />
                            <FontAwesomeIcon
                                onClick={() => {
                                    navigate(
                                        `/marketplace/admin/templates/${row.id}/preview`
                                    );
                                }}
                                icon={faEye}
                            />
                            <FontAwesomeIcon
                                className="delete-button"
                                icon={faTrashCan}
                                onClick={() => {
                                    setSelectedTemplate(row);
                                    setIsRemoveConfirmationPopupVisible(true);
                                }}
                            />
                        </div>
                    </>
                );
            },
        },
    ];

    return (
        <>
            <div className="timeline-templates">
                <div>
                    <div className="mb-3">
                        <strong>Timeline Templates</strong>

                        <Button
                            type="button"
                            className="btn btn-primary ml-3"
                            onClick={() => {
                                navigate(`/marketplace/admin/templates/new`);
                            }}>
                            Create New
                        </Button>
                    </div>
                    <div
                        role="tabpanel"
                        id="panel-0"
                        aria-labelledby="tab-0"
                        aria-hidden="false">
                        <div className="card-body">
                            {isLoading ? (
                                Helpers.renderTableLoader()
                            ) : (
                                <BootstrapTable
                                    striped
                                    bordered
                                    bootstrap4
                                    remote
                                    keyField="id"
                                    data={templates}
                                    columns={templatesColumns}
                                    defaultSorted={defaultSorted}
                                    filter={filterFactory()}
                                    pagination={paginationFactory({
                                        page,
                                        sizePerPage,
                                        totalSize,
                                        tooltipTargetId:
                                            'timeline-templates-tooltip',
                                    })}
                                    onTableChange={handleTableChange}
                                    noDataIndication="No Templates available"
                                    rowStyle={{ height: '44px' }}
                                />
                            )}
                        </div>
                    </div>
                </div>
                {selectedTemplate && isRenamingPopupVisible && (
                    <TimelineCopyModal
                        isVisible={isRenamingPopupVisible}
                        template={selectedTemplate}
                        onClose={(id: number | undefined) => {
                            if (!!id)
                                navigate(`/marketplace/admin/templates/${id}`);
                            else {
                                setIsRenamingPopupVisible(false);
                                setSelectedTemplate(undefined);
                            }
                        }}
                        fetchData={() => fetchTimelineTemplates()}
                    />
                )}
                {selectedTemplate && isRemoveConfirmationPopupVisible && (
                    <ConfirmationModal
                        isVisible={isRemoveConfirmationPopupVisible}
                        setIsVisible={setIsRemoveConfirmationPopupVisible}
                        header="Confirmation"
                        confirmationText={`Are you sure you want to remove '${selectedTemplate.name}' Timeline Template?`}
                        nextButtonText="Confirm"
                        cancelButtonText="Back"
                        onConfirm={removeTemplate}
                        params={selectedTemplate.id}
                    />
                )}
            </div>
        </>
    );
};

export default TimelineTemplates;
